import api from "../../core/api";
import { Page } from "../../shared/models/models";

export interface AdminMachine {
  id: string;
  model: string;
  serialNumber: string;
  internalNumber: string;
  productionYear?: number;
  description: string;
  system: string;
  version: string;
  botActivated: boolean;

  customerId: string;
  customerName: string;

  typeId: string;
  typeName: string;

  manufacturerId: string;
  manufacturerName: string;
  customBrandName: string;

  operationTypeId?: string;
  operationTypeName?: string;

  axisConfigurationId?: string;
  axisConfigurationName?: string;

  controlSystemBrandId?: string;
  controlSystemBrandName?: string;

  automationLevelId?: string;
  automationLevelName?: string;

  createdDate: string;
  modifiedDate: string;
  statusDate: string;
  openServiceReportsCount: number;
}

export interface AdminMachinesQuery {
  pageNo: number;
  pageSize: number;
  search?: string;
  customerId?: string;
  manufacturerId?: string;
  typeId?: string;
  botActivated?: boolean;
  sortBy?: number;
  sortDesc?: boolean;
}

export interface SetBotActivationRequest {
  isActivated: boolean;
}

/**
 * Convert sort parameter enum to backend field name
 */
const convertSortByToFieldName = (sortBy?: number): string | undefined => {
  if (sortBy === undefined) return undefined;

  const sortByMap: { [key: number]: string } = {
    0: "CustomerName",
    1: "ManufacturerName",
    2: "Model",
    3: "TypeName",
    4: "BotActivated",
    5: "CreatedDate",
    6: "ModifiedDate"
  };

  return sortByMap[sortBy] || "CreatedDate";
};

/**
 * Get paginated list of all machines (admin view)
 */
export const getAdminMachines = async (
  query: AdminMachinesQuery
): Promise<Page<AdminMachine>> => {
  const params = new URLSearchParams();
  params.append("pageNo", query.pageNo.toString());
  params.append("pageSize", query.pageSize.toString());

  if (query.search) params.append("search", query.search);
  if (query.customerId) params.append("customerId", query.customerId);
  if (query.manufacturerId) params.append("manufacturerId", query.manufacturerId);
  if (query.typeId) params.append("typeId", query.typeId);
  if (query.botActivated !== undefined && query.botActivated !== null) {
    params.append("botActivated", query.botActivated.toString());
  }

  const sortByField = convertSortByToFieldName(query.sortBy);
  if (sortByField) params.append("sortBy", sortByField);

  if (query.sortDesc !== undefined) {
    params.append("sortDesc", query.sortDesc.toString());
  }

  const response = await api.get<Page<AdminMachine>>(
    `/v1/admin/machines?${params.toString()}`,
    {
      baseURL: (window as any).app.config.machineryApiBaseUrl
    }
  );

  return response.data;
};

/**
 * Get single machine details (admin view)
 */
export const getAdminMachine = async (id: string): Promise<AdminMachine> => {
  const response = await api.get<AdminMachine>(`/v1/admin/machines/${id}`, {
    baseURL: (window as any).app.config.machineryApiBaseUrl
  });

  return response.data;
};

/**
 * Enable or disable bot activation for a machine
 */
export const setBotActivation = async (
  id: string,
  isActivated: boolean
): Promise<void> => {
  await api.put(
    `/v1/admin/machines/${id}/bot-activation`,
    { isActivated },
    {
      baseURL: (window as any).app.config.machineryApiBaseUrl
    }
  );
};
